import { StyleSheet, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
} from 'react-native-reanimated';

import { ConfettiAnimation } from 'pages/cart/cart-item/redeem-reward/confetti-animation';
import type { Tier } from 'pages/loyalty/loyalty-profile/profile-card/components/hooks/use-progress-calculation';
import { primitive } from 'styles/constants/primitives';

import { ProgressMarkers } from './progress-markers';

interface ProgressBarProps {
  tiers: Tier[];
  widthSV: any;
  showConfettiAnimation: boolean;
  currentPoints: number;
  firstTierPoints: number;
}

// UI settings
const progressBarRadius = 8;
const markerPadding = 11;
const markerContainerWidth = markerPadding * 2;
const confettiOffset = 38;
const minInitialProgressWidth = 5;
const maxInitialProgressWidth = markerPadding;

export const ProgressBar = ({
  tiers,
  widthSV,
  showConfettiAnimation,
  currentPoints,
  firstTierPoints,
}: ProgressBarProps) => {
  const containerW = useSharedValue(0);

  // Derived values
  const translateX = useDerivedValue(() => {
    'worklet';
    const progressPercentage = Math.max(0, Math.min(100, widthSV.value));
    const contentW = containerW.value - markerPadding * 2;

    // Don't show bar until we have real width measurement
    if (contentW <= 0) {
      return -9999;
    }

    // If user has points, but less than the first tier points (first marker), show the progress between
    // the range of minInitialProgressWidth and maxInitialProgressWidth
    if (currentPoints < firstTierPoints) {
      const initialProgressPercentage = currentPoints / firstTierPoints;
      const initialProgressWidth = markerPadding * 2 * initialProgressPercentage;

      const val =
        initialProgressWidth === 0
          ? 0
          : Math.max(
              minInitialProgressWidth,
              Math.min(maxInitialProgressWidth, initialProgressWidth)
            );

      return -containerW.value + val;
    }

    return contentW * (progressPercentage / 100 - 1);
  });

  const confettiTranslateX = useDerivedValue(() => {
    'worklet';
    return translateX.value - confettiOffset;
  });

  // Animated styles
  const fillStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const confettiStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: confettiTranslateX.value }],
  }));

  return (
    <View style={styles.progressBarContainer}>
      <View style={styles.progressBarTrack}>
        <ProgressMarkers tiers={tiers} />
      </View>

      <View
        style={styles.progressBarFillContainer}
        onLayout={({
          nativeEvent: {
            layout: { width },
          },
        }) => {
          containerW.value = width;
        }}
      >
        <Animated.View style={[styles.progressBarFill, fillStyle]}>
          {currentPoints >= firstTierPoints && <View style={styles.progressBarFillStart} />}
          <View style={styles.progressBarFillEnd}></View>
        </Animated.View>
      </View>
      {showConfettiAnimation && (
        <View style={styles.confettiContainer}>
          <Animated.View style={[styles.confettiWrapper, confettiStyle]}>
            <ConfettiAnimation />
          </Animated.View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  progressBarContainer: {
    marginBottom: 2,
    backgroundColor: primitive.$blackOpacity10,
    paddingRight: markerPadding,
    paddingLeft: markerPadding,
    borderRadius: progressBarRadius,
  },
  progressBarTrack: { position: 'relative', height: 16 },
  progressBarFillContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    paddingRight: markerPadding,
    paddingLeft: markerPadding,
    height: '100%',
    borderRadius: progressBarRadius,
    overflow: 'hidden',
  },
  progressBarFillStart: {
    position: 'absolute',
    top: 0,
    left: -markerPadding,
    width: markerPadding,
    height: '100%',
    backgroundColor: primitive.bk.$meltyYellow,
  },
  progressBarFillEnd: {
    position: 'absolute',
    top: 0,
    right: -markerContainerWidth / 2,
    width: markerContainerWidth / 2,
    height: '100%',
    backgroundColor: primitive.bk.$meltyYellow,
    borderTopRightRadius: progressBarRadius,
    borderBottomRightRadius: progressBarRadius,
    borderLeftWidth: 1,
    borderLeftColor: primitive.bk.$meltyYellow,
    boxSizing: 'content-box',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: primitive.bk.$meltyYellow,
    width: '100%',
  },
  confettiContainer: {
    position: 'absolute',
    top: 0,
    left: markerPadding,
    right: markerPadding,
    bottom: 0,
  },
  confettiWrapper: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 10,
    justifyContent: 'center',
  },
});
